import React, { useEffect, useState } from 'react';
import { FileText, Link as LinkIcon, Clock } from 'lucide-react';
import { api } from '../../services/api';

interface DocumentsTabProps {
    entityId: string;
    projectId: string;
}

interface DocumentItem {
    id: string;
    title: string;
    source: 'jira-story' | 'confluence-page' | 'manual-pdf' | 'confluence-pdf' | 'unknown';
    url?: string;
    chunks_count: number;
    updated_at?: string;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({ entityId, projectId }) => {
    const [documents, setDocuments] = useState<DocumentItem[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadDocuments();
    }, [entityId, projectId]);

    const loadDocuments = async () => {
        setLoading(true);
        try {
            const data = await api.getEvidenceDocuments(entityId, projectId);
            setDocuments(data);
        } catch (e) {
            console.error("Error loading documents", e);
        } finally {
            setLoading(false);
        }
    };

    const getSourceIcon = (source: string) => {
        if (source.includes('pdf')) return <FileText className="w-4 h-4 text-red-400" />;
        if (source.includes('jira')) return <div className="w-4 h-4 bg-blue-500 rounded-sm flex items-center justify-center text-[8px] font-bold text-white">J</div>;
        if (source.includes('confluence')) return <div className="w-4 h-4 bg-blue-400 rounded-sm flex items-center justify-center text-[8px] font-bold text-white">C</div>;
        return <FileText className="w-4 h-4 text-slate-400" />;
    };

    const formatSource = (source: string) => {
        switch (source) {
            case 'jira-story': return 'Jira Story';
            case 'confluence-page': return 'Confluence Page';
            case 'manual-pdf': return 'PDF Upload';
            case 'confluence-pdf': return 'PDF (Confluence)';
            default: return source;
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (documents.length === 0) {
        return (
            <div className="text-center py-12 border border-dashed border-slate-700 rounded-lg">
                <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-6 h-6 text-slate-500" />
                </div>
                <h3 className="text-slate-300 font-medium mb-2">No documents found</h3>
                <p className="text-slate-500 text-sm max-w-sm mx-auto">
                    No documents have been indexed for this entity yet. Trigger ingestion to fetch data from Jira or upload a PDF.
                </p>
            </div>
        );
    }

    return (
        <div className="overflow-hidden rounded-lg border border-slate-800">
            <table className="w-full text-left text-sm">
                <thead className="bg-slate-900 border-b border-slate-800 text-slate-400">
                    <tr>
                        <th className="px-4 py-3">Source</th>
                        <th className="px-4 py-3">Document Title / Key</th>
                        <th className="px-4 py-3 text-right">Chunks Found</th>
                        <th className="px-4 py-3 text-right">Last Indexed</th>
                        <th className="px-4 py-3 w-10"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-800 bg-slate-900/50">
                    {documents.map((doc, i) => (
                        <tr key={i} className="hover:bg-slate-800/50 group">
                            <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                    {getSourceIcon(doc.source)}
                                    <span className="text-slate-300 capitalize">{formatSource(doc.source)}</span>
                                </div>
                            </td>
                            <td className="px-4 py-3 text-white font-medium">
                                {doc.url ? (
                                    <a href={doc.url} target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 flex items-center gap-1">
                                        {doc.title}
                                        <LinkIcon className="w-3 h-3 opacity-50" />
                                    </a>
                                ) : (
                                    doc.title
                                )}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-300">
                                <span className="bg-slate-800 px-2 py-0.5 rounded text-xs border border-slate-700">
                                    {doc.chunks_count} chunks
                                </span>
                            </td>
                            <td className="px-4 py-3 text-right text-slate-500 text-xs">
                                <div className="flex items-center justify-end gap-1">
                                    <Clock className="w-3 h-3" />
                                    {doc.updated_at ? new Date(doc.updated_at).toLocaleDateString() : '-'}
                                </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                                {/* Future Action: Download or View */}
                                {doc.url && (
                                    <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-white">
                                        <LinkIcon className="w-4 h-4" />
                                    </a>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default DocumentsTab;
